import React from 'react';
import { Video, Monitor, Users, TrendingUp, Shield, Headphones } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Telehealth = () => {
  const benefits = [
    {
      icon: Monitor,
      title: 'Remote Healthcare',
      description: 'Connect patients and providers seamlessly through digital platforms.',
    },
    {
      icon: Headphones,
      title: '24/7 Support',
      description: 'Round-the-clock telehealth services for continuous patient care.',
    },
    {
      icon: Shield,
      title: 'Secure Platform',
      description: 'HIPAA-compliant technology ensuring patient data security.',
    },
    {
      icon: TrendingUp,
      title: 'Scalable Solutions',
      description: 'Grow your telehealth services as your practice expands.',
    },
  ];

  const features = [
    {
      title: 'Customized',
      description: 'We provide solutions that suit your requirements at best.',
    },
    {
      title: 'Together, We are One',
      description: 'We intend to be Your Strategic Partner and Adviser.',
    },
    {
      title: 'Growth & Expansion',
      description: 'We scale your requirements.',
    },
  ];

  return (
    <div className="telehealth-page">
      <PageMeta
        title="Telehealth"
        description="Telehealth staffing solutions: remote healthcare, 24/7 support, HIPAA-compliant platforms. Scale your telehealth practice with BluekraftMed."
        path="/telehealth"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Video className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-5xl font-bold mb-6">Telehealth</h1>
            <p className="text-xl leading-relaxed">
              Modern digital healthcare solutions connecting patients and providers
            </p>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-cyan-600 font-semibold mb-2">WHY US?</p>
            <div className="w-16 h-1 bg-cyan-600 mx-auto mb-8"></div>
            <h2 className="text-4xl font-bold mb-6">Customized & Precise Attention!</h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                As the society around us evolves, we too embrace the revolution around us. The Telehealth global market continues to grow over the last few years and its significance in the modern world continues to be recognised by major leaders and clients.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                We provide remote services as well. Telehealth is a digital support system that enables your institution to:
              </p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
                <CardHeader>
                  <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <CardTitle>{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-cyan-600 font-semibold mb-2">WHY US?</p>
            <div className="w-16 h-1 bg-cyan-600 mx-auto mb-8"></div>
            <h2 className="text-4xl font-bold mb-6">
              You are just a call away from your Customized and Precise Telehealth services!
            </h2>
            <p className="text-xl text-gray-600 mb-4">Get onboard!</p>
            <p className="text-gray-600 text-lg mb-6 leading-relaxed">
              As we draw from our large pool of candidates, our team focuses on the specific skills and expertise required for long-term success.
            </p>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              We tailor each search to the specific needs of your practice or facility, and we work with you to find the best physician candidate.
            </p>
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-6 text-lg">
              Get Started Today
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-2xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-lg">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <img
              src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&h=600&fit=crop"
              alt="Telehealth Services"
              className="rounded-lg shadow-xl w-full"
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Implement Telehealth?</h2>
            <p className="text-xl mb-8">
              Contact us today to discuss your telehealth staffing and technology needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-white text-cyan-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
              </Button>
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-cyan-600 px-8 py-6 text-lg">
                Call: +1 (469)-466-2313
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Telehealth;